import { useState } from 'react';

import { OfflineState, SecondaryActionButton, StonePanel } from '@aztec-realms/ui';

import { clearOfflineRealmSnapshot, type OfflineRealmSnapshot } from './data/offline-realm';

export function OfflineRealmScreen({
  snapshot,
  onCleared,
}: {
  snapshot?: OfflineRealmSnapshot;
  onCleared: () => void;
}) {
  const [clearing, setClearing] = useState(false);

  async function clearSavedView() {
    setClearing(true);
    try {
      await clearOfflineRealmSnapshot();
      onCleared();
    } finally {
      setClearing(false);
    }
  }

  return (
    <section className="offline-realm" data-testid="offline-read-only-view">
      <OfflineState />
      {snapshot ? (
        <>
          <header>
            <span className="realm-screen__eyebrow">Saved on this device</span>
            <h1>{snapshot.settlement.name}</h1>
            <p>
              Settlement tier {snapshot.settlement.visualTier} · level {snapshot.settlement.level}
            </p>
            <small>Last refreshed {new Date(snapshot.cachedAt).toLocaleString()}</small>
          </header>
          <StonePanel className="offline-realm__panel" ornament={2}>
            <h2>Settlement overview</h2>
            <ul>
              {snapshot.settlement.buildings.map((building, index) => (
                <li key={`${building.label}-${index}`}>
                  <strong>{building.label}</strong>
                  <span>
                    Level {building.level} · {building.state.replaceAll('_', ' ')}
                  </span>
                </li>
              ))}
            </ul>
          </StonePanel>
          <StonePanel className="offline-realm__panel" ornament={1}>
            <h2>Saved Codex metadata</h2>
            <ul>
              {snapshot.codexFamilies.map((family) => (
                <li key={family.label}>
                  <strong>{family.label}</strong>
                  <span>
                    {family.decodedCount}/{family.totalCount} decoded · {family.masteryTier}
                  </span>
                </li>
              ))}
            </ul>
          </StonePanel>
          <p className="offline-realm__boundary" role="status">
            No moves, purchases, location claims, rewards or balances are stored or finalized in
            this view. Reconnect to refresh from the authoritative server.
          </p>
          <SecondaryActionButton disabled={clearing} onClick={() => void clearSavedView()}>
            {clearing ? 'Forgetting saved view…' : 'Forget saved view on this device'}
          </SecondaryActionButton>
        </>
      ) : (
        <StonePanel className="offline-realm__panel" ornament={2}>
          <h1>No saved realm is available</h1>
          <p>Reconnect once to prepare a private read-only view for this device.</p>
        </StonePanel>
      )}
    </section>
  );
}
